/**
 * STUN Test Utility
 *
 * This utility helps verify that STUN can discover your public IP address
 * when behind NAT. Run this in the browser console or integrate into your app.
 */

interface StunTestResult {
  success: boolean;
  publicIP?: string;
  candidateType?: string;
  error?: string;
}

interface TestActiveCallStunResult {
  localCandidates: {
    type: string;
    ip: string;
    port: number;
    candidateType: string;
    protocol: string;
  }[];
  remoteCandidates: {
    type: string;
    ip: string;
    port: number;
    candidateType: string;
    protocol: string;
  }[];
  activePair: {
    localIP: string;
    remoteIP: string;
    state: string;
    localCandidateType: string;
    remoteCandidateType: string;
  };
}

export interface ComprehensiveStunTestResult {
  results: {
    server: string;
    result: StunTestResult;
  }[];
  summary: {
    working: number;
    failed: number;
    publicIPs: string[];
  };
}

/**
 * Test STUN connectivity using Google's public STUN server
 * @param {string} stunServer - Optional STUN server URL (defaults to Google's)
 * @returns {Promise<StunTestResult>} Promise with test results
 */
async function testStunConnectivity(
  stunServer = 'stun:stun.l.google.com:19302'
): Promise<StunTestResult> {
  return new Promise((resolve) => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: stunServer }],
    });

    const timeout = setTimeout(() => {
      pc.close();
      resolve({
        success: false,
        error: 'STUN test timed out after 5 seconds',
      });
    }, 5000);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidate = event.candidate.candidate;

        // Parse candidate string to extract IP and type
        // Format: "candidate:1 1 UDP 2130706431 192.168.1.1 54321 typ host"
        // or: "candidate:2 1 UDP 1694498815 PUBLIC_IP 54321 typ srflx raddr 192.168.1.1 rport 54321"

        if (candidate.includes('typ srflx')) {
          // Server reflexive candidate - STUN is working!
          clearTimeout(timeout);
          const ipMatch = candidate.match(/(\d+\.\d+\.\d+\.\d+)/);
          const publicIP = ipMatch ? ipMatch[1] : undefined;

          pc.close();
          resolve({
            success: true,
            publicIP,
            candidateType: 'srflx',
          });
        } else if (candidate.includes('typ host')) {
          // Host candidate - local IP (store but don't resolve yet)
          // We'll wait for srflx candidate or timeout
        }
      } else {
        // No more candidates - check if we got any srflx candidates
        // If not, STUN may be blocked
        clearTimeout(timeout);
        pc.close();
        resolve({
          success: false,
          error:
            'No server reflexive candidates found - STUN may be blocked or unreachable',
        });
      }
    };

    // Create a data channel to trigger ICE gathering
    pc.createDataChannel('test');
    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch((error: DOMException) => {
        clearTimeout(timeout);
        pc.close();
        resolve({
          success: false,
          error: `Error creating offer: ${error.message}`,
        });
      });
  });
}

/**
 * Test STUN using the active peer connection from a call
 * @param {RTCPeerConnection} peerConnection - Active RTCPeerConnection from a call
 * @returns {Promise<TestActiveCallStunResult>} Promise with candidate information
 */
async function testActiveCallStun(
  peerConnection: RTCPeerConnection
): Promise<TestActiveCallStunResult> {
  const stats = await peerConnection.getStats();
  const localCandidates: TestActiveCallStunResult['localCandidates'] = [];
  const remoteCandidates: TestActiveCallStunResult['remoteCandidates'] = [];
  let activePair: TestActiveCallStunResult['activePair'] | undefined;

  stats.forEach((report) => {
    if (report.type === 'local-candidate') {
      localCandidates.push({
        type: report.type,
        ip: report.ip,
        port: report.port,
        candidateType: report.candidateType,
        protocol: report.protocol,
      });
    } else if (report.type === 'remote-candidate') {
      remoteCandidates.push({
        type: report.type,
        ip: report.ip,
        port: report.port,
        candidateType: report.candidateType,
        protocol: report.protocol,
      });
    } else if (
      report.type === 'candidate-pair' &&
      report.state === 'succeeded'
    ) {
      // Find the local and remote candidates for the active pair
      const localCandidate = stats.get(report.localCandidateId);
      const remoteCandidate = stats.get(report.remoteCandidateId);

      if (localCandidate && remoteCandidate) {
        activePair = {
          localIP: localCandidate.ip,
          remoteIP: remoteCandidate.ip,
          state: report.state,
          localCandidateType: localCandidate.candidateType,
          remoteCandidateType: remoteCandidate.candidateType,
        };
      }
    }
  });

  return {
    localCandidates,
    remoteCandidates,
    activePair,
  };
}

/**
 * Comprehensive STUN connectivity test
 * Tests multiple STUN servers and provides detailed results
 * @returns {Promise<ComprehensiveStunTestResult>} Promise with test results from multiple servers
 */
async function comprehensiveStunTest(): Promise<ComprehensiveStunTestResult> {
  const stunServers = ['stun:stun.l.google.com:19302'];

  const results = await Promise.all(
    stunServers.map(async (server) => ({
      server,
      result: await testStunConnectivity(server),
    }))
  );

  const working = results.filter((r) => r.result.success).length;
  const failed = results.filter((r) => !r.result.success).length;
  const publicIPs = results.map((r) => r.result.publicIP).filter((ip) => !!ip);

  return {
    results,
    summary: {
      working,
      failed,
      publicIPs: [...new Set(publicIPs)], // Remove duplicates
    },
  };
}

/**
 * Format STUN test results into a human-readable string
 * @param {ComprehensiveStunTestResult} testResults - The test results to format
 * @returns {string} Formatted string with test results
 */
function formatStunTestResults(
  testResults: ComprehensiveStunTestResult
): string {
  const timestamp = new Date().toISOString();
  let output = `STUN Connectivity Test Results\n`;
  output += `Timestamp: ${timestamp}\n`;
  output += `=`.repeat(60) + '\n\n';

  output += `Summary:\n`;
  output += `  Working STUN servers: ${testResults.summary.working}/${testResults.results.length}\n`;
  output += `  Failed STUN servers: ${testResults.summary.failed}/${testResults.results.length}\n`;
  output += `  Public IPs discovered: ${
    testResults.summary.publicIPs.join(', ') || 'None'
  }\n\n`;

  output += `Detailed Results:\n`;
  output += `-`.repeat(60) + '\n';

  testResults.results.forEach((serverResult, index) => {
    output += `\n${index + 1}. ${serverResult.server}\n`;
    output += `   Status: ${
      serverResult.result.success ? '[SUCCESS ✓]' : '[FAILED ✗]'
    }\n`;

    if (serverResult.result.success) {
      output += `   Public IP: ${serverResult.result.publicIP}\n`;
      output += `   Candidate Type: ${serverResult.result.candidateType}\n`;
    } else {
      output += `   Error: ${serverResult.result.error}\n`;
    }
  });

  output += `\n` + `=`.repeat(60) + '\n';
  output += `\nInterpretation:\n`;

  if (testResults.summary.working > 0) {
    output += `✓ STUN is working correctly. Your network allows WebRTC connections.\n`;
    output += `  Public IP(s): ${testResults.summary.publicIPs.join(', ')}\n`;
    output += `  NAT traversal should work for VoIP calls.\n`;
  } else {
    output += `✗ STUN is not working. Possible issues:\n`;
    output += `  • Firewall blocking UDP traffic\n`;
    output += `  • STUN servers unreachable (port 19302)\n`;
    output += `  • Corporate network restrictions\n`;
    output += `  • VPN or proxy interfering with WebRTC\n\n`;
    output += `  Recommended actions:\n`;
    output += `  1. Check firewall settings (allow UDP 19302, 1024-65535)\n`;
    output += `  2. Contact network administrator\n`;
    output += `  3. Test on a different network\n`;
  }

  return output;
}

// Export for ES modules (React/TypeScript)
export {
  testStunConnectivity,
  testActiveCallStun,
  comprehensiveStunTest,
  formatStunTestResults,
};
